import { useEffect, useState } from 'react';
import {
  and,
  assoc,
  assocPath,
  compose,
  defaultTo,
  dissoc,
  dissocPath,
  equals,
  find,
  includes,
  not,
  omit,
  pathEq,
  pathOr,
  prop,
  propEq,
  when
} from 'ramda';
import { SignatureModeEnum } from '@fasstech/oip-core/entities/Signature/enums';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import useSaveSouscriptionPaymentRefundMutation from './useSaveSouscriptionPaymentRefundMutation';
import { getLabelValueFromTable } from '@@viewHelpers/format';

const useSouscriptionPaymentRefundForm = ({ middleFeatures, form }) => {
  const [loading, setLoading] = useState(false);

  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const { processHandlers, context } = useProcess();

  const { onFormSubmit, loading: isFetchingForm } = useSaveSouscriptionPaymentRefundMutation();

  const {
    PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_CHEQUE_AUTORISE,
    PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_PRELEVEMENT_AUTORISE,
    PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_VIREMENT_AUTORISE,
    PAIEMENTETREMBOURSEMENT_JOUR_PRELEVEMENT_VALEUR,
    PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_PRELEVEMENT_VALEUR,
    PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_VIREMENT_VALEUR
  } = middleFeatures;

  const {
    formState: { isValid },
    watch,
    setValue
  } = form;

  const paymentTypeSelected = watch('bankAccounts.0.type');
  const paymentFrequencySelected = watch('bankAccounts.0.debitFrequency');
  const frequencyValuesTable =
    paymentTypeSelected === 'VIREMENT'
      ? PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_VIREMENT_VALEUR
      : PAIEMENTETREMBOURSEMENT_FREQUENCE_PAIEMENT_PRELEVEMENT_VALEUR;

  const paymentFrequencyOptions = getLabelValueFromTable(
    'NOM',
    'CODE',
    frequencyValuesTable
  );

  const paymentDayOptions = getLabelValueFromTable(
    'NOM',
    'CODE',
    PAIEMENTETREMBOURSEMENT_JOUR_PRELEVEMENT_VALEUR
  );

  const paymentDayIsVisible = compose(
    equals('true'),
    defaultTo('true'),
    prop('AFFICHAGE_JOUR_PRELEVEMENT'),
    find(propEq('CODE', paymentFrequencySelected))
  )(frequencyValuesTable || []);

  useEffect(() => {
    if (!paymentTypeSelected) {
      if (equals(true, PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_PRELEVEMENT_AUTORISE)) {
        setValue('bankAccounts.0.type', 'PRELEVEMENT');
      } else if (equals(true, PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_CHEQUE_AUTORISE)) {
        setValue('bankAccounts.0.type', 'CHEQUE');
      } else if (equals(true, PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_VIREMENT_AUTORISE)) {
        setValue('bankAccounts.0.type', 'VIREMENT');
      }
    }
  }, [
    PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_PRELEVEMENT_AUTORISE,
    PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_CHEQUE_AUTORISE,
    PAIEMENTETREMBOURSEMENT_TYPE_PAIEMENT_VIREMENT_AUTORISE
  ]);

  const $onPrevClick = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const onSubmit = (values) => {
    if (loading) return;
    setLoading(true);
    values = compose(
      omit(['radioDisplayRefundInformations', 'thirdPartyPayer']),
      when(
        () =>
          includes(pathOr('PRELEVEMENT', ['bankAccounts', 0, 'type'], values), [
            'CHEQUE',
            'VIREMENT'
          ]),
        compose(
          dissocPath(['bankAccounts', 0, 'debitDay']),
          dissocPath(['bankAccounts', 0, 'ownerName']),
          dissocPath(['bankAccounts', 0, 'bankName']),
          dissocPath(['bankAccounts', 0, 'iban']),
          dissocPath(['bankAccounts', 0, 'bic'])
        )
      ),
      when(
        () =>
          and(
            equals(false, prop('radioDisplayRefundInformations', values)),
            equals('PRELEVEMENT', pathOr('PRELEVEMENT', ['bankAccounts', 0, 'type'], values))
          ),
        dissocPath(['customer', 'bankAccounts'])
      ),
      when(
        () => equals(true, prop('thirdPartyPayer', values)),
        compose(
          dissoc('bankAccounts'),
          assocPath(['contacts', 0, 'bankAccounts'], prop('bankAccounts', values)),
          assoc('contacts', [])
        )
      )
    )(values);

    const isElectronicSignature = pathEq(
      ['signature', 'mode'],
      SignatureModeEnum.ELECTRONIC,
      values
    );

    if (isElectronicSignature && not(displayConfirmationModal)) {
      setLoading(false);
      return setDisplayConfirmationModal(true);
    }

    onFormSubmit(values, (ok, error) => {
      if (ok && !error) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      } else {
        setLoading(false);
      }
    });
  };

  return {
    onSubmit,
    isValid,
    displayConfirmationModal,
    setDisplayConfirmationModal,
    $onPrevClick,
    isFetchingForm,
    paymentDayOptions,
    paymentFrequencyOptions,
    paymentDayIsVisible
  };
};

export default useSouscriptionPaymentRefundForm;
